// src/App.js
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from "react-router-dom";
import Home from "./pages/Home";
import GamePage from "./pages/GamePage";
import Admin from "./pages/Admin";
import Download from "./pages/Download";
import Account from "./pages/Account";
import NewsHub from "./pages/NewsHub";
import EmailSupport from "./pages/EmailSupport";
import ReportIssue from "./pages/ReportIssue";
import TermsConditions from "./pages/TermsConditions";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import UserAgreement from "./pages/UserAgreement";
import ContentProvider from "./pages/ContentProvider";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import "./styles.css";
import logoOcto from "./assets/logo-octo.png";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { collection, doc, getDoc, onSnapshot, query, where } from "firebase/firestore";
import avatarOptions from "./data/avatarOptions";
import { ACTIVE_GAME_SOURCE_KEY, ACTIVE_GAME_SOURCE_NAME } from "./data/games";
import { connectPresence } from "./utils/presence";

const THEME_STORAGE_KEY = "up-theme";
const ACTIVE_SOURCE_URL =
  ACTIVE_GAME_SOURCE_KEY === "gm"
    ? "https://gamemonetize.com/"
    : "https://octogames.io/";

/** Scroll window to top on route change (e.g. opening a game page). */
const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const getInitialTheme = () => {
  if (typeof window === "undefined") return "dark";
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (stored === "light" || stored === "dark") return stored;
  const prefersLight = window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: light)").matches;
  return prefersLight ? "light" : "dark";
};

const AppHeader = ({ theme, onToggleTheme, user, userAvatar, unreadNewsCount }) => {
  const avatarSrc =
    userAvatar ||
    user?.photoURL ||
    "https://api.dicebear.com/7.x/bottts/svg?seed=octo-default";

  return (
    <header className="up-header">
      <div className="up-header-inner">
        <div className="up-header-left">
          <Link to="/" className="up-logo">
            <img src={logoOcto} alt="Octo" className="up-logo-img" />
          </Link>
        </div>
        <div className={`up-header-right ${user ? "up-header-right-user" : "up-header-right-guest"}`}>
          {!user && (
            <Link to="/account?mode=signup" className="up-play-now-btn up-play-now-mobile">
              <span className="up-play-now-icon">▶</span>
              <span className="up-play-now-label">Play Now</span>
            </Link>
          )}
          <button
            type="button"
            className="up-theme-icon-btn"
            onClick={onToggleTheme}
            aria-label="Toggle theme"
          >
            <span className="up-theme-icon">
              {theme === "light" ? "☀" : "🌙"}
            </span>
          </button>
          <Link to="/download" className="up-download-btn">
            <span className="up-download-icon">↓</span>
            <span className="up-download-label-desktop">Download APK</span>
            <span className="up-download-label-mobile">APK</span>
          </Link>
          <Link to="/news" className="up-news-btn" aria-label="News Hub">
            <span className="up-news-icon">📰</span>
            <span className="up-news-label">News Hub</span>
            {user && unreadNewsCount > 0 && (
              <span className="up-news-badge" aria-label={`${unreadNewsCount} unread news`}>
                {unreadNewsCount > 99 ? "99+" : unreadNewsCount}
              </span>
            )}
          </Link>
          {user ? (
            <Link to="/account" className="up-user" aria-label="Profile">
              <img src={avatarSrc} alt="Profile avatar" className="up-user-avatar" />
            </Link>
          ) : (
            <div className="up-auth-actions">
              <Link to="/account?mode=signin" className="up-auth-link">
                <span className="up-auth-link-icon">👤</span>
                <span className="up-auth-link-label">Sign in</span>
              </Link>
              <Link to="/account?mode=signup" className="up-auth-signup">
                <span className="up-auth-signup-icon">✨</span>
                <span className="up-auth-signup-label">Sign up</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

const AppFooter = () => {
  const year = new Date().getFullYear();
  return (
    <footer className="up-footer">
      <div className="up-footer-inner">
        <div className="up-footer-grid">
          <div className="up-footer-legal-side">
            <div className="up-footer-brand">
              <img src={logoOcto} alt="OctoGames" className="up-footer-logo" />
              <div className="up-footer-brand-text">
                <strong>OctoGames.io</strong>
                <span>Play online instantly</span>
              </div>
            </div>
            <p className="up-footer-copy">© 2023-{year} OctoGames.io</p>
            <p className="up-footer-side-text">
              OctoGames.io is an HTML5 game portal with embedded games,
              player profiles, favorites, and session tracking.
            </p>
            <p className="up-footer-side-text">
              Disclaimer: We use embedded HTML5 games provided by{" "}
              <a
                href={ACTIVE_SOURCE_URL}
                target="_blank"
                rel="noreferrer"
                className="up-footer-link-external"
              >
                {ACTIVE_GAME_SOURCE_NAME}
              </a>.
            </p>
          </div>

          <div className="up-footer-columns">
            <nav className="up-footer-col" aria-label="Games">
              <h4 className="up-footer-col-title">Games</h4>
              <Link to="/" className="up-footer-link">Home</Link>
              <Link to="/" className="up-footer-link">Featured Banners</Link>
              <Link to="/" className="up-footer-link">Recommended Games</Link>
              <Link to="/" className="up-footer-link">All Games Catalog</Link>
            </nav>

            <nav className="up-footer-col" aria-label="Player">
              <h4 className="up-footer-col-title">Player</h4>
              <Link to="/account" className="up-footer-link">Profile</Link>
              <Link to="/account" className="up-footer-link">Favorites</Link>
              <Link to="/account" className="up-footer-link">Recent Sessions</Link>
              <Link to="/download" className="up-footer-link">Download APK</Link>
            </nav>

            <nav className="up-footer-col" aria-label="Support">
              <h4 className="up-footer-col-title">Support</h4>
              <Link to="/blog" className="up-footer-link">Blog</Link>
              <Link to="/news" className="up-footer-link">News</Link>
              <Link to="/support/email" className="up-footer-link">Email Support</Link>
              <Link to="/support/report-issue" className="up-footer-link">Report Issue</Link>
              <a href="https://instagram.com/" target="_blank" rel="noreferrer" className="up-footer-link">
                Instagram
              </a>
              <a href="https://x.com/" target="_blank" rel="noreferrer" className="up-footer-link">
                Twitter
              </a>
            </nav>

            <nav className="up-footer-col" aria-label="Legal">
              <h4 className="up-footer-col-title">Legal</h4>
              <Link to="/legal/terms" className="up-footer-link">Terms & Conditions</Link>
              <Link to="/legal/privacy" className="up-footer-link">Privacy Policy</Link>
              <Link to="/legal/user-agreement" className="up-footer-link">User Agreement</Link>
              <Link to="/legal/content-provider" className="up-footer-link">Content Provider</Link>
            </nav>

          </div>
        </div>
      </div>
    </footer>
  );
};

const App = () => {
  const [theme, setTheme] = useState(getInitialTheme);
  const [user, setUser] = useState(null);
  const [userAvatar, setUserAvatar] = useState("");
  const [authReady, setAuthReady] = useState(false);
  const [unreadNewsCount, setUnreadNewsCount] = useState(0);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    if (theme === "light") {
      root.setAttribute("data-theme", "light");
    } else {
      root.removeAttribute("data-theme");
    }
    if (typeof window !== "undefined") {
      window.localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
  }, [theme]);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (nextUser) => {
      setUser(nextUser || null);
      connectPresence(nextUser || null);
      if (!nextUser) {
        setUserAvatar("");
        setUnreadNewsCount(0);
        setAuthReady(true);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "users", nextUser.uid));
        const avatarId = snap.exists() ? snap.data()?.avatarId : "";
        const avatar = avatarOptions.find((a) => a.id === avatarId);
        setUserAvatar(avatar?.url || nextUser.photoURL || "");
      } catch {
        setUserAvatar(nextUser.photoURL || "");
      } finally {
        setAuthReady(true);
      }
    });
    return () => unsub();
  }, []);
  useEffect(() => {
    if (!user?.uid) {
      setUnreadNewsCount(0);
      return;
    }

    let publishedNewsIds = [];
    let readNewsById = {};

    const recomputeUnread = () => {
      if (publishedNewsIds.length === 0) {
        setUnreadNewsCount(0);
        return;
      }
      const unread = publishedNewsIds.reduce(
        (count, newsId) => (readNewsById[newsId] ? count : count + 1),
        0
      );
      setUnreadNewsCount(unread);
    };

    const unsubUser = onSnapshot(
      doc(db, "users", user.uid),
      (snap) => {
        const data = snap.exists() ? snap.data() : {};
        readNewsById =
          data?.readNewsById && typeof data.readNewsById === "object" ? data.readNewsById : {};
        recomputeUnread();
      },
      () => {
        readNewsById = {};
        recomputeUnread();
      }
    );

    const unsubNews = onSnapshot(
      query(collection(db, "newsPosts"), where("published", "==", true)),
      (snap) => {
        publishedNewsIds = snap.docs.map((newsDoc) => newsDoc.id);
        recomputeUnread();
      },
      () => {
        publishedNewsIds = [];
        recomputeUnread();
      }
    );

    return () => {
      unsubUser();
      unsubNews();
    };
  }, [user]);

  const handleToggleTheme = () => {
    setTheme((prev) => (prev === "light" ? "dark" : "light"));
  };

  return (
    <Router>
      <div className="up-app-root">
        {!authReady ? (
          <div className="up-app-loader" role="status" aria-live="polite">
            <img src={logoOcto} alt="OctoGames" className="up-app-loader-logo" />
            <div className="up-app-loader-spinner" />
            <p className="up-app-loader-text">Loading OctoGames...</p>
          </div>
        ) : (
          <>
            <AppHeader
              theme={theme}
              onToggleTheme={handleToggleTheme}
              user={user}
              userAvatar={userAvatar}
              unreadNewsCount={unreadNewsCount}
            />
            <main className="up-app-main">
              <ScrollToTop />
              <Routes>
                <Route path="/genre/:genreSlug" element={<Home />} />
                <Route path="/" element={<Home />} />
                <Route path="/game/:slug" element={<GamePage />} />
                <Route path="/download" element={<Download />} />
                <Route path="/news" element={<NewsHub />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/account" element={<Account />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/support/email" element={<EmailSupport />} />
                <Route path="/support/report-issue" element={<ReportIssue />} />
                <Route path="/legal/terms" element={<TermsConditions />} />
                <Route path="/legal/privacy" element={<PrivacyPolicy />} />
                <Route path="/legal/user-agreement" element={<UserAgreement />} />
                <Route path="/legal/content-provider" element={<ContentProvider />} />
              </Routes>
            </main>
            <AppFooter />
          </>
        )}
      </div>
    </Router>
  );
};

export default App;